// Angular + external
import {
  Component,
  OnInit,
  ViewChild,
  Inject,
  Optional,
  forwardRef,
  AfterViewInit,
} from "@angular/core";
// BFX
import { BfxConfigService } from "../../shared/auth-services/system-config/bfx-config.service";
import { BfxDashboardMenuService } from "../bfx-common/services/bfx-dashboard-menu.service";
import { GoldenLayoutService } from "../golden-layout/golden-layout-service";
import { TranslateService } from "@ngx-translate/core";
import { BfxUserSettingsService } from "../bfx-user-settings/bfx-user-settings.service";
import { TieredMenu } from "@bfx/primeng/tieredmenu";
import { BfxBaseBlotter } from "app/bfx-base-blotter/bfx-base-blotter";
import { BfxBlotterOrders } from "app/bfx-blotter-orders/bfx-blotter-orders";
import { BfxLadderOrders } from "app/bfx-ladder-orders/bfx-ladder-orders";
import { BfxTradeBlotterComponent } from "../bfx-blotter-trade/bfx-trade-blotter.component";

import { SysFeatures, SystemConfigPromiseService } from "shared/cubiq-services";
import { NativeDesktopSupport } from "../bfx-native-desktop/native-desktop-support";
import { BfxAfterViewInitPromise } from "../bfx-common/helpers/bfx-after-view-init-promise";
import { BfxBlotterInbox } from "../bfx-blotter-inbox/bfx-blotter-inbox";
import { BfxAlgoMonitor } from "app/bfx-algo-monitor/bfx-algo-monitor";

export enum AmountViewType {
  MILLIONS,
  NOTIONAL,
}

@Component({
  selector: "bfx-blotter-settings-menu",
  templateUrl: "./html/bfx-blotter-settings-menu.html",
  providers: [],
  standalone: false,
})
export class BfxBlotterSettingsMenuComponent
  implements OnInit, AfterViewInit, BfxAfterViewInitPromise
{
  afterViewInitResolve?: Function;

  afterViewInitReady: Promise<void> = new Promise<void>((res) => {
    this.afterViewInitResolve = res;
  });

  /***************************************/
  /* DATA */
  public menuItems: Array<any> = [];

  private _targetDocument: any = null;

  private ndfEnabled: boolean = false;

  @ViewChild(TieredMenu) menu: TieredMenu;

  /**********************************/
  /* CONSTRUCTOR + LIFE-CYCLE HOOKS*/

  constructor(
    @Optional()
    @Inject(forwardRef(() => BfxBaseBlotter))
    private _blotter: BfxBaseBlotter,
    private _configService: BfxConfigService,
    private dashboardMenuService: BfxDashboardMenuService,
    private userSettingsService: BfxUserSettingsService,
    private translationService: TranslateService,
    private goldenLayoutService: GoldenLayoutService,
    private sysConfigService: SystemConfigPromiseService,
  ) {
    this.sysConfigService.getSystemConfig(SysFeatures.ndfEnabled).then((value) => {
      this.ndfEnabled = "Y" == value;
    });
  }

  ngOnInit() {
    // Do the initialization on toggle instead
    // this.menuItems = this.constructMenu();
  }

  ngOnDestroy() {}

  ngAfterViewInit(): void {
    this.afterViewInitResolve();
    if (NativeDesktopSupport.isNativeDesktopContainer()) {
      // FIXME: NG8
      // NativeDesktopSupport.wrapDomHandler(this.menu.domHandler, true);
      let menu: any = this.menu;
      menu._appendOverlay = this.menu.appendOverlay;
      menu.appendOverlay = this.appendMenuOverlay.bind(this);

      menu._onOverlayAnimationStart = this.menu.onOverlayAnimationStart;
      this.menu.onOverlayAnimationStart = this.onOverlayAnimationStart.bind(this);
    }
  }

  private onOverlayAnimationStart($event: any): void {
    let menu: any = this.menu;

    menu._onOverlayAnimationStart($event);

    if (NativeDesktopSupport.isNativeDesktopContainer()) {
      if (
        this._targetDocument != null &&
        this.menu.container != null &&
        this._targetDocument !== this.menu.container.ownerDocument
      ) {
        let menu: any = this.menu;
        menu._document = this._targetDocument;

        this._targetDocument.body.appendChild(this.menu.container);
      }
    }
  }

  private appendMenuOverlay(): void {
    let menu: any = this.menu;

    if (menu.appendTo === "body" && menu._document != null) {
      menu._document.body.appendChild(menu.container);
    } else {
      menu._appendOverlay();
    }
  }

  /**********************************/
  /* INITIALIZATION */

  /**********************************/
  /* PUBLIC Methods */

  public settings(event: Event): void {
    if (this._blotter instanceof BfxBlotterOrders) {
      (this._blotter as BfxBlotterOrders).showSettings(event);
    }
  }

  public pauseRates(): void {
    if (this._blotter instanceof BfxBlotterOrders) {
      (this._blotter as BfxBlotterOrders).pauseRates();
    }
  }

  public resumeRates(): void {
    if (this._blotter instanceof BfxBlotterOrders) {
      (this._blotter as BfxBlotterOrders).resumeRates();
    }
  }

  public saveAsDefault(): void {
    if (this._blotter) {
      this._blotter.saveAsDefault();
    }
  }

  public switchNdfSpotTracking(): void {
    if (this._blotter instanceof BfxBlotterOrders) {
      this._blotter.toggleNdfSpotTracking();
    }
  }

  public switchBaseDealt(): void {
    if (this._blotter instanceof BfxBlotterOrders) {
      this._blotter.toggleBaseDealt();
    }
  }

  public toggleCcyTray(): void {
    if (this._blotter instanceof BfxLadderOrders) {
      this._blotter.toggleCcyPairTray();
    }
  }

  public toggle($event: any): void {
    this.afterViewInitReady.then(() => {
      if (NativeDesktopSupport.isNativeDesktopContainer() && $event.target != null) {
        this._targetDocument = $event.target.ownerDocument;

        if (
          this._targetDocument != null &&
          this.menu.container != null &&
          this._targetDocument !== this.menu.container.ownerDocument
        ) {
          let menu: any = this.menu;
          menu._document = this._targetDocument;

          if (this.menu.container.offsetParent) {
            this.menu.container.parentElement.removeChild(this.menu.container);
          }
        }

        this.menu.bindDocumentClickListener = () => {};
        this.menu.unbindDocumentClickListener();
        this.menu.documentClickListener = this.menu.renderer.listen(
          this._targetDocument,
          "click",
          () => {
            if (!this.menu.preventDocumentDefault) {
              this.menu.hide();
            }
            this.menu.preventDocumentDefault = false;
          },
        );
      }

      this.menuItems = this.constructMenu();
      this.menu.toggle($event);
    });
  }

  /**********************************/
  /* PRIVATE Methods */

  protected constructMenu(): Array<any> {
    var menuItems: Array<any> = [];

    if (this._blotter instanceof BfxBlotterOrders) {
      let determineNdfSpotTrackingTitle: string =
        this._blotter.OrdersTableMode.BASE === this._blotter.mode ||
        this._blotter.OrdersTableMode.DEALT === this._blotter.mode
          ? "NDF"
          : "Spot";
      let determineBaseDealtTitle: string =
        this._blotter.OrdersTableMode.DEALT === this._blotter.mode ||
        this._blotter.OrdersTableMode.NDF_DEALT === this._blotter.mode
          ? "Base"
          : "Dealt";

      // Settings
      menuItems.push({
        label: "Settings",
        icon: "bfx-icon-sm icon_ion_settings_filled",
        title: "Settings",
        command: (event) => this.settings(event),
      });

      if (this.ndfEnabled && !(this._blotter instanceof BfxAlgoMonitor)) {
        // Spot/NDF Tracking
        menuItems.push({
          label: `Enable ${determineNdfSpotTrackingTitle} Tracking`,
          icon: `bfx-icon-sm icon-base-delt ${
            determineNdfSpotTrackingTitle === "Spot" ? "icon-toggle-spot" : "icon-toggle-ndf"
          }`,
          title: `Switch to ${determineNdfSpotTrackingTitle} Tracking`,
          command: (event) => this.switchNdfSpotTracking(),
        });
      }

      // Base/Dealt Mode
      if (
        !(this._blotter instanceof BfxLadderOrders) &&
        !(this._blotter instanceof BfxAlgoMonitor)
      ) {
        menuItems.push({
          label: `Enable ${determineBaseDealtTitle} Mode`,
          icon: `bfx-icon-sm icon-base-delt ${
            determineBaseDealtTitle === "Dealt" ? "icon-toggle-dealt" : "icon-toggle-base"
          }`,
          title: `Switch to view orders in ${determineBaseDealtTitle} terms`,
          command: (event) => this.switchBaseDealt(),
        });
      }
    }

    // if (this._blotter instanceof BfxTradeBlotterComponent) {
    //   // Tarde Blotter
    //   // Settings
    //   menuItems.push({
    //     label: "Settings",
    //     icon: "bfx-icon-sm icon_ion_settings_filled",
    //     title: "Settings",
    //     command: (event) => this.settings(event),
    //   });
    // }

    // Show/Hide Currency Pair Tray
    if (this._blotter instanceof BfxLadderOrders) {
      menuItems.push({
        label: `${this._blotter.showFullTray ? "Hide" : "Show"} Ccy Pair Tray`,
        icon: "fa icon-ion_fit_all",
        title: `${
          this._blotter.showFullTray ? "Hide" : "Show"
        } the Currency Pair Tray at the bottom of the ladder.`,
        command: (event) => this.toggleCcyTray(),
      });
    }

    // Pause/Resume Rates
    if (this._blotter instanceof BfxBlotterOrders) {
      let areRatesPaused: boolean = (this._blotter as BfxBlotterOrders).service.areRatesPaused();
      menuItems.push({
        label: `${areRatesPaused ? "Resume" : "Pause"} Rates`,
        icon: `bfx-icon-sm ${areRatesPaused ? "icon-bfx_activated" : "icon-pause_circle"}`,
        title: `${areRatesPaused ? "Resume" : "Pause"} Rate updates in the Blotter/Ladder. `,
        command: (event) => (areRatesPaused ? this.resumeRates() : this.pauseRates()),
      });
    }

    // Save as Default - Inbox should not display it. DI and BM Summary too, but they do not show or are not part of this settings menu
    if (!(this._blotter instanceof BfxBlotterInbox)) {
      menuItems.push({
        label: "Save as Default",
        icon: "bfx-icon-sm icon-save",
        title: `Save the current ${
          this._blotter instanceof BfxLadderOrders ? "Ladder" : "Blotter"
        } as the default layout. This will be applied to new ${
          this._blotter instanceof BfxLadderOrders ? "Ladders" : "Blotters"
        }.`,
        command: (event) => this.saveAsDefault(),
      });
    }

    return menuItems;
  }
}
